import React from 'react'
// Functions for working with mission data
import { TIME_SLOTS } from '../../../options'
import {
  getMissionTimeslot,
  simplifyWeatherData,
  formatTime,
} from '../../../utils'
import { getWeatherForLocationInRange } from 'Utils/Weather'
import ApplyingIcon from '../../../icons/ApplyingIcon'
import { getLoactionCvr } from '../../../api'

export const handleLocationChange = (setCurrentData, savedData) => (
  dayId,
  missionIndex,
  newLocation,
) => {
  setCurrentData(prevData =>
    prevData.map(day => {
      if (day.id === dayId) {
        const updatedMissions = [...day.missions]
        const currentMission = updatedMissions[missionIndex]

        // Find the original mission from savedData to compare
        const originalDay = savedData.find(d => d.id === dayId)
        const originalMission =
          originalDay &&
          originalDay.missions &&
          originalDay.missions[missionIndex]

        // Check if location actually changed by comparing with original saved data
        const newLocationId = newLocation.id || newLocation.locationId
        const originalLocationId = originalMission && originalMission.locationId
        const hasLocationChanged = originalLocationId !== newLocationId

        // Create the base mission update
        const baseMissionUpdate = {
          ...currentMission,
          locationId: newLocationId,
          name: newLocation.name,
          isEvent: newLocation.isEvent,
          isSheltered: newLocation.isSheltered,
        }

        // Remove newLocationData if exists and location is back to original
        if (currentMission.newLocationData && !hasLocationChanged) {
          const {
            newLocationData,
            ...missionWithoutNewData
          } = baseMissionUpdate
          updatedMissions[missionIndex] = missionWithoutNewData
        } else if (hasLocationChanged) {
          // Add newLocationData only if location changed from original
          updatedMissions[missionIndex] = {
            ...baseMissionUpdate,
            newLocationData: {
              latitude: newLocation.latitude,
              longitude: newLocation.longitude,
              hasLocationChanged: true,
              day: dayId,
              isEvent: newLocation.isEvent,
              isSheltered: newLocation.isSheltered,
              locationId: newLocationId,
            },
          }
        } else {
          // No change from original, just update the basic fields
          updatedMissions[missionIndex] = baseMissionUpdate
        }

        // Recalculate mission count by time slots (taking basPerMission into account)
        const morningMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.MORNING)
          .reduce((sum, m) => sum + m.basPerMission, 0)
        const lunchMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.LUNCH)
          .reduce((sum, m) => sum + m.basPerMission, 0)
        const afternoonMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.AFTERNOON)
          .reduce((sum, m) => sum + m.basPerMission, 0)

        return {
          ...day,
          missions: updatedMissions,
          morningMissions,
          lunchMissions,
          afternoonMissions,
        }
      }
      return day
    }),
  )
}

export const handleMissionBaChange = setCurrentData => (
  dayId,
  missionIndex,
  newBaValue,
) => {
  setCurrentData(prevData =>
    prevData.map(day => {
      if (day.id === dayId) {
        const updatedMissions = [...day.missions]
        updatedMissions[missionIndex] = {
          ...updatedMissions[missionIndex],
          basPerMission: newBaValue,
        }

        // Recalculate mission count by time slots (taking basPerMission into account)
        const morningMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.MORNING)
          .reduce((sum, m) => sum + m.basPerMission, 0)
        const lunchMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.LUNCH)
          .reduce((sum, m) => sum + m.basPerMission, 0)
        const afternoonMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.AFTERNOON)
          .reduce((sum, m) => sum + m.basPerMission, 0)

        return {
          ...day,
          missions: updatedMissions,
          morningMissions,
          lunchMissions,
          afternoonMissions,
        }
      }
      return day
    }),
  )
}

export const handleMissionTimeChange = (
  setCurrentData,
  convertTimeToSimpleFormat,
  city,
) => (dayId, missionIndex, newTime) => {
  // Convert time to simple format "08:00"
  const convertedTime = convertTimeToSimpleFormat(newTime)

  // Determine new timeSlot
  const newTimeSlot = getMissionTimeslot({ city, time: convertedTime })

  setCurrentData(prevData =>
    prevData.map(day => {
      if (day.id === dayId) {
        const updatedMissions = [...day.missions]
        updatedMissions[missionIndex] = {
          ...updatedMissions[missionIndex],
          time: convertedTime,
          timeSlot: newTimeSlot,
        }

        // Recalculate mission count by time slots (taking basPerMission into account)
        const morningMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.MORNING)
          .reduce((sum, m) => sum + m.basPerMission, 0)
        const lunchMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.LUNCH)
          .reduce((sum, m) => sum + m.basPerMission, 0)
        const afternoonMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.AFTERNOON)
          .reduce((sum, m) => sum + m.basPerMission, 0)

        return {
          ...day,
          missions: updatedMissions,
          morningMissions,
          lunchMissions,
          afternoonMissions,
        }
      }
      return day
    }),
  )
}

export const handleApplyGlobalBaPerMission = (
  setCurrentData,
  baPerMission,
  runSnackBar,
) => () => {
  setCurrentData(prevData => {
    const updatedData = prevData.map(day => {
      const updatedMissions = day.missions.map(mission => ({
        ...mission,
        basPerMission: baPerMission,
      }))

      // Recalculate mission count by time slots (taking basPerMission into account)
      const morningMissions = updatedMissions
        .filter(m => m.timeSlot === TIME_SLOTS.MORNING)
        .reduce((sum, m) => sum + m.basPerMission, 0)
      const lunchMissions = updatedMissions
        .filter(m => m.timeSlot === TIME_SLOTS.LUNCH)
        .reduce((sum, m) => sum + m.basPerMission, 0)
      const afternoonMissions = updatedMissions
        .filter(m => m.timeSlot === TIME_SLOTS.AFTERNOON)
        .reduce((sum, m) => sum + m.basPerMission, 0)

      return {
        ...day,
        missions: updatedMissions,
        morningMissions,
        lunchMissions,
        afternoonMissions,
      }
    })

    return updatedData
  })

  runSnackBar({
    type: 'success',
    msg: `BAs per mission updated to ${baPerMission} for all missions!`,
  })
}

export const handleDeleteDay = (
  setCurrentData,
  onCloseModal,
  runSnackBar,
) => day => {
  setCurrentData(prevData => prevData.filter(d => d.id !== day.id))
  onCloseModal()
  runSnackBar({
    type: 'success',
    msg: `Date deleted!`,
  })
}

export const handleDeleteLocation = (
  setCurrentData,
  onCloseModal,
  runSnackBar,
) => (location, dayId) => {
  setCurrentData(prevData =>
    prevData.map(day => {
      if (day.id === dayId) {
        const updatedMissions = day.missions.filter(
          mission =>
            mission.locationId !== location.locationId ||
            mission.time !== location.time,
        )

        const morningMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.MORNING)
          .reduce((sum, m) => sum + m.basPerMission, 0)
        const lunchMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.LUNCH)
          .reduce((sum, m) => sum + m.basPerMission, 0)
        const afternoonMissions = updatedMissions
          .filter(m => m.timeSlot === TIME_SLOTS.AFTERNOON)
          .reduce((sum, m) => sum + m.basPerMission, 0)

        return {
          ...day,
          missions: updatedMissions,
          morningMissions,
          lunchMissions,
          afternoonMissions,
        }
      }
      return day
    }),
  )

  onCloseModal()
  runSnackBar({
    type: 'success',
    msg: `Mission deleted!`,
  })
}

export const handleCancel = (
  savedData,
  originalBaPerMission,
  originalPayAdjustment,
  setCurrentData,
  handleBaPerMissionChange,
  handlePayAdjustmentChange,
  setIsEdit,
  setData,
) => () => {
  // Simply restore the original savedData (which shouldn't have newLocationData)
  // No need to clean anything - savedData is already clean
  setCurrentData(savedData)
  handleBaPerMissionChange(originalBaPerMission)
  handlePayAdjustmentChange(originalPayAdjustment)

  setIsEdit(false)
}

export const handleSave = (
  currentData,
  baPerMission,
  payAdjustment,
  setSavedData,
  setOriginalBaPerMission,
  setOriginalPayAdjustment,
  setIsEdit,
  runSnackBar,
  setData,
  city,
  setModalProps,
  setCurrentData,
  campaignId,
) => async () => {
  // Show loading modal
  setModalProps({
    isOpen: true,
    title: 'Applying changes',
    description: (
      <span>
        Please wait while we are applying your changes <br /> and updating
        weather data for new locations.
      </span>
    ),
    icon: <ApplyingIcon />,
    isHideCloseButton: true,
  })

  try {
    // Find missions with changed locations and update their weather data
    const updatedData = await Promise.all(
      currentData.map(async day => {
        const updatedMissions = await Promise.all(
          day.missions.map(async mission => {
            if (
              mission.newLocationData &&
              mission.newLocationData.hasLocationChanged
            ) {
              // Prepare weather API request parameters
              const weatherParams = {
                latitude: mission.newLocationData.latitude,
                longitude: mission.newLocationData.longitude,
                from: day.id,
                to: day.id,
                cityId: city.id,
              }

              // Fetch weather data for the new location
              const weatherData = await getWeatherForLocationInRange(
                weatherParams,
              )

              const simplifiedWeatherData = simplifyWeatherData(
                weatherData.days,
              )
              const weather = simplifiedWeatherData[0]

              // Remove the temporary newLocationData and add weather + draftPayAdjustment
              const { newLocationData, ...missionWithoutTempData } = mission

              const payloadToGetCVR = {
                locationId: newLocationData.locationId,
                cityId: city.id,
                cityName: city.name,
                campaignId: campaignId,
                morningStart: formatTime(city.morningCheckInTime),
                lunchMissionsStart: formatTime(city.lunchCheckInTime),
                afternoonStart: formatTime(city.afternoonCheckInTime),
                date: day.id,
                time: missionWithoutTempData.time,
                isEvent: newLocationData.isEvent,
                isSheltered: newLocationData.isSheltered,
              }

              const response = await getLoactionCvr({
                missions: [payloadToGetCVR],
              })
              const predictedCvr = response.missions[0].predictedCvr
                ? (response.missions[0].predictedCvr * 100).toFixed(2)
                : null

              return {
                ...missionWithoutTempData,
                weather,
                draftPayAdjustment: payAdjustment,
                predictedCvr,
                predictedCvrUnformatted: response.missions[0].predictedCvr,
              }
            }

            // Add draftPayAdjustment to missions that didn't change location
            return {
              ...mission,
              draftPayAdjustment: payAdjustment,
            }
          }),
        )

        return {
          ...day,
          missions: updatedMissions,
        }
      }),
    )

    // Update the current data with weather information
    setCurrentData(updatedData)
    setSavedData(updatedData)
    setOriginalBaPerMission(baPerMission)
    setOriginalPayAdjustment(payAdjustment)

    if (setData) {
      setData(updatedData)
    }

    setIsEdit(false)
    setModalProps(prev => ({ ...prev, isOpen: false }))

    runSnackBar({
      type: 'success',
      msg: 'Changes saved successfully!',
    })
  } catch (error) {
    console.error('Error saving changes:', JSON.stringify(error))
    setModalProps(prev => ({ ...prev, isOpen: false }))

    setModalProps({
      isOpen: true,
      title: 'Error saving changes',
      description: <span>Something went wrong. Please notify support.</span>,
      onClose: prev => ({ ...prev, isOpen: false }),
    })

    runSnackBar({
      type: 'error',
      msg: 'Error saving changes. Please try again.',
    })
  }
}
